// 租户配置相关API
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiClient } from './client'
import type { CompanySettings, CompanyStyle } from '@/types/auth.types'

// 租户配置API响应类型
export interface CompanySettingsResponse {
  settings: CompanySettings
}

export interface UpdateCompanySettingsRequest {
  settings: Partial<CompanySettings>
}

// 查询键工厂
export const companyKeys = {
  all: ['company'] as const,
  settings: () => [...companyKeys.all, 'settings'] as const,
  style: () => [...companyKeys.all, 'style'] as const,
}

// 获取租户配置
export const useCompanySettings = () => {
  return useQuery({
    queryKey: companyKeys.settings(),
    queryFn: async () => {
      const response = await api.get<CompanySettingsResponse>('/companies/me/settings')
      return response
    },
    staleTime: 5 * 60 * 1000, // 5分钟缓存
  })
}

// 更新租户配置
export const useUpdateCompanySettings = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (settings: Partial<CompanySettings>) => {
      const response = await api.put<{ message: string; settings: CompanySettings }>(
        '/companies/me/settings',
        settings
      )
      return response
    },
    onSuccess: (data) => {
      // 更新缓存
      queryClient.setQueryData(companyKeys.settings(), { settings: data.settings })
      // 使相关查询失效
      queryClient.invalidateQueries({ queryKey: companyKeys.all })
    },
  })
}

// 知识库文档状态响应类型
export interface KnowledgeBaseDocumentsStatus {
  has_documents: boolean
  document_count: number
  company_id: number
}

// 检查知识库是否有文档
export const useKnowledgeBaseHasDocuments = () => {
  return useQuery({
    queryKey: [...companyKeys.all, 'knowledge-base-documents'],
    queryFn: async () => {
      const response = await api.get<KnowledgeBaseDocumentsStatus>('/knowledge/has-documents')
      return response
    },
    staleTime: 30 * 1000, // 30秒缓存，频繁检查
  })
}

// 风格配置相关API响应类型
export interface CompanyStyleResponse {
  style_config: CompanyStyle;
}

// 获取公司风格配置
export const useCompanyStyle = () => {
  return useQuery({
    queryKey: companyKeys.style(),
    queryFn: async () => {
      const response = await api.get<CompanyStyleResponse>('/companies/me/style');
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5分钟缓存
  });
};

// 导出租户配置（下载 ZIP）
export const exportTenant = async (companyId: number, includeKb: boolean = false) => {
  // apiClient 响应拦截器已解包 response.data，直接返回 Blob
  const blob = await apiClient.get(`/companies/${companyId}/export`, {
    params: { include_kb: includeKb },
    responseType: 'blob',
  })
  return blob as unknown as Blob
}

// 导入租户配置（上传 ZIP）
export interface ImportTenantSummary {
  company: string
  settings: string
  connectors: { created: number; updated: number }
  data_sources: { created: number; updated: number }
  platform_integrations: { created: number; updated: number }
  users: { created: number; skipped: number }
  knowledge_base: { imported: number }
}

export const importTenant = async (file: File): Promise<{ success: boolean; summary: ImportTenantSummary }> => {
  const formData = new FormData()
  formData.append('file', file)
  // apiClient 响应拦截器已解包 response.data
  const result = await apiClient.post('/companies/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return result as unknown as { success: boolean; summary: ImportTenantSummary }
}

// 导出租户 mutation hook
export const useExportTenant = () => {
  return useMutation({
    mutationFn: async ({ companyId, includeKb }: { companyId: number; includeKb: boolean }) => {
      return exportTenant(companyId, includeKb)
    },
  })
}

// 导入租户 mutation hook
export const useImportTenant = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      return importTenant(file)
    },
  })
}

// 更新公司风格配置
export const useUpdateCompanyStyle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (styleConfig: CompanyStyle) => {
      const response = await api.put<{ message: string }>(
        '/companies/me/style',
        { style_config: styleConfig }
      );
      return response;
    },
    onSuccess: () => {
      // 使风格配置缓存失效
      queryClient.invalidateQueries({ queryKey: companyKeys.style() });
    },
  });
};
